/**
 * Encodes an object into a query string.
 * @param obj - The object to encode into a query string.
 * @param stringify - An optional custom stringify function.
 * @returns The encoded query string.
 * @example
 * ```
 * // Example input: encode({ token: 'foo', key: 'value' })
 * // Expected output: "token=foo&key=value"
 * ```
 */
export function encode(obj: Record<string, any>, stringify: (value: any) => string = String): string {
  const result = new URLSearchParams();

  for (const key in obj) {
    const val = obj[key];

    if (val !== undefined) {
      result.set(key, stringify(val));
    }
  }

  return result.toString();
}

export function stringifySearchWith(stringify: (search: any) => string, parser?: (str: string) => any) {
  const hasParser = typeof parser === 'function';

  function stringifyValue(val: any) {
    if (typeof val === 'object' && val !== null) {
      try {
        return stringify(val);
      } catch (_err) {
        // silent
      }
    } else if (hasParser && typeof val === 'string') {
      try {
        // Check if it's a valid parseable string.
        // If it is, then stringify it again.
        parser(val);

        return stringify(val);
      } catch (_err) {
        // silent
      }
    }

    return val;
  }

  return (search: Record<string, any>) => {
    const searchStr = encode(search, stringifyValue);

    return searchStr ? `?${searchStr}` : '';
  };
}
